import { useEffect } from "preact/hooks";
import { SidebarTrigger } from "../components/Sidebar";
import { formatRemaining } from "../lib/duration";
import { me, refreshMe, session } from "../state";

function formatDate(value: string | null): string {
  if (!value) return "";
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** "N of M questions left today", plus when the next one unlocks. */
function allowanceLine(
  questionsLeft: number | null | undefined,
  dailyLimit: number | null | undefined,
  nextAvailableAt: string | null | undefined,
): string {
  if (questionsLeft == null) return "Questions unavailable right now.";
  if (questionsLeft > 0) {
    return dailyLimit != null
      ? `${questionsLeft} of ${dailyLimit} questions left today.`
      : `${questionsLeft} ${
        questionsLeft === 1 ? "question" : "questions"
      } left today.`;
  }
  if (nextAvailableAt) {
    const remaining = new Date(nextAvailableAt).getTime() - Date.now();
    const when = remaining > 0 ? formatRemaining(remaining) : "any moment now";
    return `Out of questions — the next one unlocks in ${when}.`;
  }
  return "Out of questions for now.";
}

export function Profile() {
  useEffect(() => {
    void refreshMe();
  }, []);

  const profile = me.value;
  const accountEmail = session.value?.email ?? "";
  const name = profile?.name ?? accountEmail;
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  const dailyLimit = profile?.dailyLimit;

  return (
    <div class="view">
      <header class="topbar content">
        <div class="topbar__left">
          <SidebarTrigger />
        </div>
      </header>

      <main class="view__center content profile">
        <section class="profile__card profile__identity">
          {profile?.avatarUrl
            ? <img class="profile__avatar" src={profile.avatarUrl} alt="" />
            : (
              <span
                class="profile__avatar profile__avatar--initial"
                aria-hidden="true"
              >
                {initial}
              </span>
            )}
          <div class="profile__who">
            <h1 class="profile__name">{name}</h1>
            {accountEmail && <p class="profile__email">{accountEmail}</p>}
            <p class="profile__allowance">
              {allowanceLine(
                profile?.questionsLeft,
                dailyLimit,
                profile?.nextAvailableAt,
              )}
            </p>
          </div>
        </section>

        <section class="profile__card">
          <h2 class="profile__section-title">How it works</h2>
          <p class="profile__note">
            FaB Judge is free to use. You get {dailyLimit ?? "a set number of"}
            {" "}questions every 24 hours, and answers that are already in the
            shared cache don't count against them.
          </p>
          {profile?.nextAvailableAt && (
            <p class="profile__note">
              Your next question unlocks{" "}
              {formatDate(profile.nextAvailableAt)}.
            </p>
          )}
        </section>
      </main>
    </div>
  );
}
